import React, {
  useId,
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import chevron_down from 'assets/icons/chevron_down.svg';
import { DialogField, DialogProps, NewDataField, PLATE_FIELD_TYPE } from './dialogInterface';
import PlateNumberInput from 'reusable/PlateNumberInput';
import { useTranslation } from 'react-i18next';
import { inModalLayer, useModalDialog } from 'reusable/useModalDialog';
import FetchFailed from 'reusable/FetchFailed';
import SearchSelect from 'reusable/RemoteSearch/SearchSelect';

const AddDialog: FC<DialogProps> = ({
  isOpen,
  onClose,
  title,
  confirmLabel,
  cancelLabel,
  fields,
  handleSubmit,
}) => {
  const { t } = useTranslation('device');
  // ADM-16。Escape で閉じる・フォーカスを中に留める・閉じたら開いた操作へ戻す。
  // RV-125。見出しの id は文書の中で一意にする（重なったダイアログが別の見出しで読まれた）。
  const headingId = useId();
  // ADM-15。保存が終わるまで確定ボタンを押せなくする（続けて押すと同じ内容を2回送っていた）。
  // EV-130・EV-131（RV-129・RV-130）。送信中（送り直し・処理中の問い合わせ直しを含む）は、
  // 入力・キャンセル・閉じる（Escape を含む）も止める。送った内容と画面が食い違ったり、
  // 結果を受け取る前に閉じたりしないため。
  const submitting = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // EV-461（#387 の RV-703 と同じ型）。作成の結果が分からないまま（前回の X-Request-ID を残した）か。入力を送った値の
  // まま固定し、「同じ内容で送り直す」か、やめるかだけにする（入力を変えると新しい X-Request-ID になり、確定していた
  // 前回の作成を取り戻せず、二重に作ったり取り違えたりするため）。開き直したら解く。
  const [unresolved, setUnresolved] = useState(false);
  const unresolvedRef = useRef(false);
  const noteId = useId();
  const closeUnlessSubmitting = () => {
    if (!submitting.current) onClose();
  };
  const dialogRef = useModalDialog(isOpen, closeUnlessSubmitting);
  // ADM-16。項目名（label）と入力欄を結び付ける（読み上げで何の欄か分かるように）。
  const fieldId = useId();
  const initializeData = useCallback(() => {
    return fields.reduce((acc, field) => {
      if (
        field.type === 'select' &&
        field.options &&
        field.options.length > 0
      ) {
        acc[field.name] = field.options[0].value ?? null;
      } else if (field.type === 'select' && field.search?.fixedOptions?.length) {
        // EV-493。検索の欄は、決まった候補の先頭を初期値にする（<select> の最初の選択肢と同じ）。
        acc[field.name] = field.search.fixedOptions[0].value;
      } else {
        acc[field.name] = null;
      }
      return acc;
    }, {} as NewDataField);
  }, [fields]);
  const [newData, setNewData] = useState<NewDataField>(initializeData());
  // EV-430（2026-10-24 作業16・18 の VoiceOver）。必須の欄は required ではなく aria-required で伝え、
  // 空のまま送ったときにアプリで確かめる。required の空欄を WebKit が開いた直後から「無効なデータ」と
  // 読んでいた。送った後に空の欄だけ aria-invalid と知らせ（role=alert）を付ける。
  const [errors, setErrors] = useState<{ [name: string]: string }>({});

  // EV-322。開いたときは初期値から始める。開いている間に欄が変わった（選択肢の読み直しなど）ときは、
  // 入れた値を残す（以前は欄が変わるたびに入力が消えた）。
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setNewData(initializeData());
      setErrors({});
      unresolvedRef.current = false;
      setUnresolved(false);
    } else if (isOpen) {
      setNewData((current) => {
        const next = initializeData();
        Object.keys(next).forEach((name) => {
          if (current[name] !== undefined && current[name] !== null) next[name] = current[name];
        });
        return next;
      });
    }
    wasOpen.current = isOpen;
  }, [isOpen, initializeData]);

  if (!isOpen) return null;

  // 欄の説明（ヘルプ・選択肢の読み込みの失敗・必須の知らせ）の id。
  const describedBy = (field: DialogField, index: number) => {
    const ids = [
      field.type !== 'select' && field.helpText ? `${fieldId}-${index}-help` : null,
      field.type === 'select' && field.fetchError && field.onRetry ? `${fieldId}-${index}-note` : null,
      errors[field.name] ? `${fieldId}-${index}-error` : null,
    ].filter(Boolean);
    return ids.length > 0 ? ids.join(' ') : undefined;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | { target: { name: string; value: string } }
  ) => {
    if (submitting.current || unresolvedRef.current) return;
    const { name, value } = e.target;
    // UAT A08。欄の値から別の欄を埋める（fillOther）。
    const field = fields.find((item) => item.name === name);
    const filled = field?.fillOther ? field.fillOther(value, String(newData[name] ?? ''), newData) : {};
    setNewData({
      ...newData,
      ...filled,
      [name]: value,
    });
    if (errors[name]) {
      setErrors((current) => {
        const next = { ...current };
        delete next[name];
        return next;
      });
    }
  };

  const handleFormSubmit = async (e: FormEvent<Element>) => {
    e.preventDefault();

    // EV-430。必須の空欄があれば送らず、欄に知らせを出して最初の空欄へフォーカスを移す。
    const missing: { [name: string]: string } = {};
    fields.forEach((field) => {
      const current = newData[field.name];
      if (field.isRequired && (current === null || current === undefined || current === '')) {
        missing[field.name] = t(field.type === 'select' ? 'ui.required-select' : 'ui.required-input', {
          ns: 'common',
        });
      }
    });
    setErrors(missing);
    const firstMissing = fields.findIndex((field) => missing[field.name]);
    if (firstMissing >= 0) {
      document.getElementById(`${fieldId}-${firstMissing}`)?.focus();
      return;
    }

    const processedData = fields.reduce(
      (acc, field) => {
        acc[field.name] = newData[field.name] || null;
        return acc;
      },
      { ...newData }
    );

    // ADM-15。入力は開き直したときに初期化する（上の useEffect）。
    // 送信のあとに戻すと、失敗して開いたままのときに入力が消えていた。
    if (submitting.current) return;
    submitting.current = true;
    setIsSubmitting(true);
    try {
      const result = await handleSubmit(e, processedData);
      const locked = Boolean(result && result.unresolved);
      unresolvedRef.current = locked;
      setUnresolved(locked);
    } finally {
      submitting.current = false;
      setIsSubmitting(false);
    }
  };

  return inModalLayer(
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div
          // PR #434 の Codex の P2。枠では切らない（状態の欄の候補の一覧が下へ開いても切れない）。角の丸みは中の面で保つ。
          className="inline-block align-bottom bg-white rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          role="dialog"
          aria-modal="true"
          aria-labelledby={headingId}
          ref={dialogRef}
          tabIndex={-1}
        >
          <div className="bg-white rounded-lg">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-left sm:mt-0 w-full">
                <div className="flex justify-between items-center mt-4 mx-6">
                  <h3
                    className="text-subtitle-1 font-medium text-neutral/500"
                    id={headingId}
                  >
                    {title}
                  </h3>
                  <button type="button"
                    onClick={closeUnlessSubmitting}
                    disabled={isSubmitting}
                    className="text-gray-600 hover:text-gray-800 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">{t('ui.close', { ns: 'common' })}</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <hr className="border-neutral/50 mt-4 w-full" />
                <form onSubmit={handleFormSubmit}>
                  {/* RV-129。送信中は入力・キャンセル・確定をまとめて無効にする。 */}
                  <fieldset disabled={isSubmitting} className="min-w-0">
                  {/* EV-461。結果が分からない間は、入力だけを送った値のまま固定する（キャンセルと送り直しは押せる）。 */}
                  <fieldset disabled={unresolved} className="min-w-0" aria-describedby={unresolved ? noteId : undefined}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-4 m-4 sm:m-6">
                    {fields.map((field, index) => (
                      <div key={index} className={field.helpText || field.type === PLATE_FIELD_TYPE ? 'sm:col-span-2' : undefined}>
                        <label
                          className="block mb-1 text-sm font-regular text-neutral/100"
                          htmlFor={`${fieldId}-${index}`}
                        >
                          {field.label}{' '}
                          <span className="text-error">
                            {field.isRequired === true && '*'}
                          </span>
                        </label>
                        {field.type === 'select' && field.search ? (
                          // EV-493。候補をサーバーで検索する欄（以前は候補を全件読んで <select> に並べていた）。
                          <SearchSelect
                            id={`${fieldId}-${index}`}
                            label={field.label}
                            describedBy={describedBy(field, index)}
                            value={newData[field.name] ? String(newData[field.name]) : ''}
                            onChange={(value) => handleChange({ target: { name: field.name, value } })}
                            fixedOptions={field.search.fixedOptions}
                            fetchPage={field.search.fetchPage}
                            knownLabels={field.search.knownLabels}
                            ariaRequired={field.isRequired}
                            invalid={Boolean(errors[field.name])}
                          />
                        ) : field.type === 'select' ? (
                          <div className="relative">
                            <select
                              name={field.name}
                              id={`${fieldId}-${index}`}
                              className="w-full mt-1 p-2 border border-neutral/50 text-neutral/30 focus:outline-none focus:border-neutral/75 text-body-2 rounded-md h-12 px-4 appearance-none"
                              aria-required={field.isRequired ? 'true' : undefined}
                              aria-invalid={errors[field.name] ? 'true' : undefined}
                              // EV-333（DS-AC1）。注記（下の FetchFailed）を出す条件とそろえる。onRetry の無い欄で在りもしない id を指さない。
                              aria-describedby={describedBy(field, index)}
                              value={newData[field.name] || ''}
                              onChange={handleChange}
                            >
                              {field.options?.map((option) => (
                                <option value={option.value} id={option.id}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-neutral/30 pr-4 ">
                              <img src={chevron_down} alt="" />
                            </div>
                          </div>
                        ) : field.type === PLATE_FIELD_TYPE ? (
                          // A07。車両ナンバーを4つの欄で入れ、「品川 330 な 38-59」に組み立てる。
                          <PlateNumberInput
                            id={`${fieldId}-${index}`}
                            value={newData[field.name] ? String(newData[field.name]) : ''}
                            onChange={(value) => handleChange({ target: { name: field.name, value } })}
                            ariaRequired={field.isRequired}
                            invalid={Boolean(errors[field.name])}
                            describedBy={describedBy(field, index)}
                          />
                        ) : (
                          <input
                            type={field.type}
                            name={field.name}
                            id={`${fieldId}-${index}`}
                            list={field.suggestions?.length ? `${fieldId}-${index}-suggestions` : undefined}
                            autoComplete={field.suggestions?.length ? 'off' : undefined}
                            className="w-full mt-1 p-2 border border-neutral/50 text-neutral/30 focus:outline-none focus:border-neutral/75 text-body-2 rounded-md h-12 px-4 appearance-none"
                            aria-required={field.isRequired ? 'true' : undefined}
                            aria-invalid={errors[field.name] ? 'true' : undefined}
                            placeholder={
                              field.type === 'date' ? 'yyyy/mm/dd' : ''
                            }
                            aria-describedby={describedBy(field, index)}
                            value={newData[field.name] || ''}
                            onChange={handleChange}
                          />
                        )}
                        {field.suggestions && field.suggestions.length > 0 ? (
                          <datalist id={`${fieldId}-${index}-suggestions`}>
                            {field.suggestions.map((suggestion) => (
                              <option key={suggestion} value={suggestion} />
                            ))}
                          </datalist>
                        ) : null}
                        {field.helpText ? (
                          <p id={`${fieldId}-${index}-help`} className="mt-1 text-caption text-neutral/100">
                            {field.helpText}
                          </p>
                        ) : null}
                        {errors[field.name] ? (
                          <p id={`${fieldId}-${index}-error`} role="alert" className="mt-1 text-caption text-error">
                            {errors[field.name]}
                          </p>
                        ) : null}
                        {field.fetchError && field.onRetry ? (
                          <FetchFailed
                            id={`${fieldId}-${index}-note`}
                            compact
                            error={field.fetchError}
                            onRetry={field.onRetry}
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>
                  </fieldset>
                  {unresolved ? (
                    <p id={noteId} className="mx-4 sm:mx-6 mb-4 text-body-2 text-neutral/300">
                      {t('operator.unresolved-note', { ns: 'layout' })}
                    </p>
                  ) : null}
                  <div className="px-4 py-3 border-t-[1px] grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center items-center rounded-md border bg-primary/50 text-subtitle-2 text-primary/300 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={closeUnlessSubmitting}
                    >
                      {cancelLabel}
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      aria-busy={isSubmitting}
                      className="w-full inline-flex justify-center items-center rounded-md bg-primary/300 text-subtitle-2 text-white h-12 disabled:opacity-60"
                    >
                      {unresolved ? t('operator.resend-same', { ns: 'layout' }) : confirmLabel}
                    </button>
                  </div>
                  </fieldset>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDialog;
